/**
 * Instant CSS-var updates in the preview iframe.
 *
 * The theme exposes every tenant-configurable color / size / toggle
 * as a CSS custom property on `:root` via `snippets/css-variables.liquid`
 * (rendered once at layout time). Any section that reads its colors
 * from `var(--foo)` instead of inline `{{ current.foo }}` will update
 * without a server round-trip — we just patch `:root` and every
 * `var(--foo)` consumer recalculates.
 *
 * This module maps global-setting ids to CSS-var names and applies
 * the patch directly on the iframe's root element. Typical edit path:
 *
 *   user drags a color picker
 *     → updateGlobalSetting('text_color', '#04ff00')
 *     → subscriber in Preview.tsx reads the diff, calls
 *       `patchCssVarsInIframe(iframe, {text_color: '#04ff00'})`
 *     → iframe.documentElement.style.setProperty('--text-color', '#04ff00')
 *     → the page's body text, nav links, icons — everything using
 *       `var(--text-color)` — flips to green within a frame.
 *
 * Settings we don't know how to map (blocks, toggles that need a
 * layout reload, etc.) fall through and the existing section re-render
 * + 600 ms layout reload flow picks them up.
 */

/**
 * Setting id → CSS var name (without the `--` prefix).
 *
 * Keep this table aligned with `snippets/css-variables.liquid`. If the
 * theme adds a new CSS var, add the mapping here so the editor's live
 * preview picks it up. When a setting doesn't map to a var (e.g. it
 * affects markup structure), leave it out — the fallback section
 * re-render handles it.
 */
/**
 * Some legacy setting ids feed MULTIPLE css vars (three aliases of the
 * same colour, for example). Array values are patched together.
 */
const SETTING_TO_CSS_VAR: Record<string, string | string[]> = {
  // Core colours
  text_color: 'text-color',
  heading_color: 'heading-color',
  background: 'background-color',
  secondary_background: 'secondary-background',
  success_color: 'success-color',
  error_color: 'error-color',

  // Header / footer
  header_background: 'header-background',
  header_text_color: 'header-text-color',
  footer_background: 'footer-background',
  footer_text_color: 'footer-text-color',

  // Buttons
  primary_button_background: 'primary-button-background',
  primary_button_text_color: 'primary-button-text-color',
  secondary_button_background: 'secondary-button-background',
  secondary_button_text_color: 'secondary-button-text-color',

  // Product card accents
  product_rating_color: 'product-rating-color',
  product_on_sale_accent: 'product-sale-accent',
  product_sold_out_accent: 'product-sold-out-accent',
  product_custom_label_background: 'product-custom-label-background',
  product_custom_label_2_background: 'product-custom-label-2-background',
  product_in_stock_text_color: 'product-in-stock-color',
  product_low_stock_text_color: 'product-low-stock-color',
  product_truck_background: 'product-truck-background',
  product_truck_color: 'product-truck-color',
  product_localization_color: 'product-localization-color',
  product_free_shipping_color: 'product-free-shipping-color',

  // Cart badge
  bolinha_sacola: 'cart-badge-background',
  bolinha_sacola_fonte: 'cart-badge-color',

  // Scarcity bar
  colorp_progress: 'scarcity-background',
  colors_progress: 'scarcity-color',
  alt_progress: 'scarcity-height',
  font_progress: 'scarcity-font-size',

  // Typography — só font-family (via other mechanism) e alinhamento sobrevivem.
  // Settings removidos: text_font_size, heading_text_transform, heading_font_weight,
  // text_font_weight, heading_font_size — alimentavam CSS vars mortas
  // (`.global-heading`/`.global-text`) que nenhuma section consumia.

  // Announcement bar (used by layout.liquid)
  anuncio_fonted: 'announcement-font-desktop',
  anuncio_alturad: 'announcement-height-desktop',
  anuncio_fontem: 'announcement-font-mobile',
  anuncio_alturam: 'announcement-height-mobile',

  // Boxes (about / trust)
  boxes_primaria: 'boxes-primaria',
  boxes_sec: 'boxes-sec',
  boxes_titulo: 'boxes-titulo',

  // Shipping box
  buscar_frete_btn: 'buscar-frete-btn',
  buscar_frete_fonte: 'buscar-frete-fonte',
  buscar_frete_local: 'buscar-frete-local',
  buscar_frete_preco: 'buscar-frete-preco',
  color_frete_fundo: 'color-frete-fundo',

  // Installments
  color_primary_parcelamento: 'parcelamento-primary',
  color_secondary_parcelamento: 'parcelamento-secondary',
  color_terciary_parcelamento: 'parcelamento-tertiary',
  color_quart_parcelamento: 'parcelamento-quart',
  color_quint_parcelamento: 'parcelamento-quint',
  borda_atv_parcelamento: 'parcelamento-border',
  borda_atv_parcelamento2: 'parcelamento-border-color',
  parcel_prod: 'parcel-prod',

  // PIX envio
  pix_envio_fundo: 'pix-envio-fundo',
  pix_envio_textdesc: 'pix-envio-textdesc',
  pix_envio_textnpix: 'pix-envio-textnpix',
  pix_envio_simb: 'pix-envio-simb',
  pix_envio_enviofundo: 'pix-envio-enviofundo',
  pix_envio_enviotexto: 'pix-envio-enviotexto',
  pix_envio_bordgeral: 'pix-envio-bordgeral',
  pix_envio_bordenvio: 'pix-envio-bordenvio',
  pix_envio_tfonte: 'pix-envio-tfonte',
  pix_envio_tsimb: 'pix-envio-tsimb',

  // WhatsApp buttons
  colorp_whats: 'whats-cor1',
  colors_whats: 'whats-cor2',
  colort_whats: 'whats-cor3',
  colorq_whats: 'whats-cor4',
  whatscompra_cor1: 'whatscompra-cor1',
  whatscompra_cor2: 'whatscompra-cor2',
  whatscompra_cor3: 'whatscompra-cor3',
  whatscompra_cor4: 'whatscompra-cor4',
  whatscompra_cor5: 'whatscompra-cor5',
  fonte_whatscompra: 'whatscompra-fonte',
  spc_whatscompra: 'whatscompra-spc',

  // Cookie bar
  cookie_fontegeral: 'cookie-fontegeral',
  cookie_fundogeral: 'cookie-fundogeral',
  cookie_aceitarfundo: 'cookie-aceitarfundo',
  cookie_aceitarfonte: 'cookie-aceitarfonte',
  cookie_recusarfundo: 'cookie-recusarfundo',
  cookie_recusarfonte: 'cookie-recusarfonte',
  cookies_fonte: 'cookies-fonte',

  // Variant selector + slideshow arrows
  varianteseletor: 'varianteseletor',
  setaslidefundo: 'setaslidefundo',
  setaslidefonte: 'setaslidefonte',
  setaslideborda: 'setaslideborda',
  setaslidebordas: 'setaslide-bordas',

  // Product labels
  labeldesconto_color: 'labeldesconto-color',
  labeldesconto_fonte: 'labeldesconto-fonte',
  fonte_vendidos: 'fonte-vendidos',
  // background_vendidos feeds three aliases in css-variables.liquid
  // (--sold-background, --back-vendidos, --background-vendidos). The
  // Liquid-rendered styles actually consume --back-vendidos, so patching
  // only `background-vendidos` did nothing until the user force-reloaded.
  background_vendidos: ['sold-background', 'back-vendidos', 'background-vendidos'],
  // Mesma situação das labels de exclusividade / mais vendido:
  // `theme.css` consome `--back-exclusivo` / `--fonte-exclusivo`
  // (sufixo "o", sem "idade"), mas `css-variables.liquid` também define
  // `--background-exclusividade` / `--fonte-exclusividade` e
  // `--exclusive-background` / `--exclusive-color`. Patcha os três pra
  // que qualquer consumidor se atualize ao vivo.
  fonte_exclusividade: ['fonte-exclusivo', 'exclusive-color', 'fonte-exclusividade'],
  background_exclusividade: ['back-exclusivo', 'exclusive-background', 'background-exclusividade'],
  fonte_exclusividade2: ['fonte-exclusivo2', 'bestseller-color', 'fonte-exclusividade2'],
  background_exclusividade2: ['back-exclusivo2', 'bestseller-background', 'background-exclusividade2'],

  // Typography sizes
  fontenavegacao: 'fontenavegacao',
  fontenavegacaom: 'fontenavegacaom',
  fonteprod: 'fonteprod',
  fonteprodm: 'fonteprodm',
  fonteprodinicial: 'fonteprodinicial',
  fonteprodinicialm: 'fonteprodinicialm',
  fontebotcompra: 'fontebotcompra',
  fontebotcompram: 'fontebotcompram',
  preco_size: 'preco-size',
  preco_sizem: 'preco-sizem',
  colecao_fonte1m: 'colecao-fonte1m',
  colecao_fonte1d: 'colecao-fonte1d',
  colecao_fonte2m: 'colecao-fonte2m',
  colecao_fonte2d: 'colecao-fonte2d',

  // Product flags
  fonte_t_bandeiras: 'fonte-t-bandeiras',
  bandeiras_w_mobile: 'bandeiras-w-mobile',
  bandeiras_w_desktop: 'bandeiras-w-desktop',

  // Discount badge
  borda_descont_pr: 'borda-descont-pr',
  fonte_descont_pr: 'fonte-descont-pr',

  // Quantity picker
  alt_botao_quantidade: 'alt-botao-quantidade',
  borda_botao_quantidade: 'borda-botao-quantidade',
  arredonda_boxes: 'arredonda-boxes',

  // Popup
  popup_border: 'popup-border',

  // Social icons
  'radius-social': 'social-radius',
  'margind-social': 'social-margind',
  'margini-social': 'social-margini',
  'cor-borda-social': 'social-cor-borda',
  'cor-icon-social': 'social-cor-icon',
  'background-icon-social': 'social-background-icon',

  // SVG icons
  icon_stroke_width: 'icon-stroke-width',

  // Checkout seal
  'selito-largura': 'selito-largura',
  'selito-larguram': 'selito-larguram',

  // Cart drawer
  checkout_button_bg: 'drawer-checkout-bg',
  checkout_button_text: 'drawer-checkout-text',
  checkout_button_border: 'drawer-checkout-border',
  continue_shopping_bg: 'drawer-continue-bg',
  continue_shopping_text: 'drawer-continue-text',
  continue_shopping_border: 'drawer-continue-border',
  free_shipping_bar_bg: 'drawer-free-ship-bar-bg',
  free_shipping_bar_color: 'drawer-free-ship-bar-color',
};

/**
 * Keys whose rendered CSS expects a `px` suffix. `--text-font-size: 14`
 * doesn't work as a CSS value; it has to be `14px`. When the source
 * setting is a plain number, tack on `px` here.
 */
const NEEDS_PX_SUFFIX = new Set<string>([
  // text_font_size removido (setting deletada do schema global)
  'alt_progress',
  'font_progress',
  'anuncio_fonted',
  'anuncio_alturad',
  'anuncio_fontem',
  'anuncio_alturam',
  // Sizes
  'fontenavegacao',
  'fontenavegacaom',
  'fonteprod',
  'fonteprodm',
  'fonteprodinicial',
  'fonteprodinicialm',
  'fontebotcompra',
  'fontebotcompram',
  'preco_size',
  'preco_sizem',
  'colecao_fonte1m',
  'colecao_fonte1d',
  'colecao_fonte2m',
  'colecao_fonte2d',
  'fonte_t_bandeiras',
  'borda_descont_pr',
  'fonte_descont_pr',
  'alt_botao_quantidade',
  'borda_botao_quantidade',
  'arredonda_boxes',
  'popup_border',
  'pix_envio_bordgeral',
  'pix_envio_bordenvio',
  'pix_envio_tfonte',
  'pix_envio_tsimb',
  'fonte_whatscompra',
  'cookies_fonte',
  'setaslidebordas',
  'borda_atv_parcelamento',
  'radius-social',
  'margind-social',
  'margini-social',
  'selito-largura',
  'selito-larguram',
]);

function formatValueForCssVar(settingId: string, raw: any): string | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'boolean') return raw ? '1' : '0';
  if (typeof raw === 'number') {
    return NEEDS_PX_SUFFIX.has(settingId) ? `${raw}px` : String(raw);
  }
  if (typeof raw === 'string') {
    // If the setting already includes a unit (e.g. "14px") keep it.
    // Otherwise append px when required.
    if (NEEDS_PX_SUFFIX.has(settingId) && /^[-+]?\d+(\.\d+)?$/.test(raw.trim())) {
      return `${raw}px`;
    }
    return raw;
  }
  return null;
}

/**
 * Mapa dinâmico auto-descoberto lendo `snippets/css-variables.liquid`
 * na sessão. Qualquer linha no formato
 *
 *     --<var-name>: {{ current.<setting_id> | default: '…' }};
 *
 * vira uma entrada aqui, unindo TODOS os aliases que o tema define para
 * a mesma setting. Isso evita que a gente tenha que manter a tabela
 * hardcoded acima em sincronia com o liquid — qualquer nova var que o
 * tema criar é patchada automaticamente ao vivo.
 *
 * Entra depois do carregamento da sessão via `setDiscoveredCssVars`.
 * Antes disso (boot do editor, primeiro render), o mapa hardcoded ainda
 * funciona como fallback.
 */
let DISCOVERED_CSS_VARS: Record<string, string[]> = {};

/**
 * Faz um parse simples do `css-variables.liquid` e monta
 * `{ settingId: [cssVarName, ...] }`.
 *
 * Suporta três formas que aparecem no tema atual:
 *   --foo: {{ current.bar | default: 'x' }};
 *   --foo: {{ current.bar }};
 *   --foo: {{ current.bar | some_filter }};
 *
 * Ignora (pra não pegar falso-positivo):
 *   - linhas dentro de comentários (/* ... * /)
 *   - `{{ settings.bar }}` (o tema usa `current`, não `settings`,
 *     na pipeline do engine pro storefront)
 */
export function discoverCssVarsFromLiquid(liquidSource: string): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  // Remove comentários de bloco CSS pra evitar matchar dentro deles.
  const stripped = liquidSource.replace(/\/\*[\s\S]*?\*\//g, '');
  const re = /--([a-zA-Z0-9_-]+)\s*:\s*\{\{\s*current\.([a-zA-Z0-9_-]+)\b/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(stripped)) !== null) {
    const varName = m[1];
    const settingId = m[2];
    if (!result[settingId]) result[settingId] = [];
    if (!result[settingId].includes(varName)) result[settingId].push(varName);
  }
  return result;
}

/**
 * Substitui o mapa dinâmico. Chamado pelo store logo após a sessão
 * abrir. Passa `{}` pra resetar (ex. trocar de tenant/tema).
 */
export function setDiscoveredCssVars(map: Record<string, string[]>) {
  DISCOVERED_CSS_VARS = map || {};
}

/*
  ===== SECTION-LEVEL CSS VARS =====

  Globais ficam no `:root` e são patchadas em `iframe.documentElement`.
  Section settings (`section.settings.product_name_color`, etc.) viram
  CSS vars escopadas em `#section-<id>` — declaradas dentro de um
  `<style>` na própria seção. Marker injector PULA `<style>`/`<script>`
  blocks (ver marker-injector.ts), então `patchSettingInDom` falha pra
  esses settings e o editor cai num re-render completo da seção =
  flicker visual.

  Solução: discoverer separado pra `section.settings.X` que produz um
  mapa `{ settingId: [{ var, unit }, ...] }`. O patcher do Preview usa
  isso pra setar `el.style.setProperty('--<var>', value+unit)`
  diretamente em `#section-<id>`. Sem re-render, sem flicker.

  Suporta unit suffix:
    --prod-name-size: {{ section.settings.product_name_size_desktop }}px;
                                                                    ^^^
  → unit = 'px', size é numérico → emit '14px'.
*/
export interface SectionCssVarBinding {
  var: string;
  unit: string;
  /**
   * `true` quando a declaração da var também aparece num bloco
   * `:root { ... }` no source da section. Isso acontece em sections
   * "config-only" tipo `cart-drawer-config` — a section em si não
   * renderiza nada visível, mas emite vars no `:root` pra que o
   * markup IRMÃO/ANCESTOR (ex.: o snippet `drawer-cart`) consiga
   * consumir via `var(--X)`.
   *
   * Quando `true`, o live-patcher TAMBÉM seta a var em
   * `documentElement` (não só no `#section-<id>`). Sem isso, mudar
   * setting do drawer não muda nada visualmente — o `:root` rule
   * declarado no `<style>` block continua com o valor antigo até o
   * próximo re-render server-side.
   */
  rootScoped: boolean;
}

/**
 * Mapa scopado por SECTION TYPE pra evitar cross-section pollution.
 *
 * Antes era `{ settingId: bindings[] }` global — quando duas sections
 * diferentes (`contact-form` + `search-results`) declaravam settings com
 * MESMO id (`background`), o patcher achava bindings de AMBAS e
 * patchava as duas vars (`--contact-bg` + `--search-bg`) toda vez que o
 * usuário mexia em qualquer "background". Vazava entre sections + dava
 * a falsa impressão de "live-patch funcionou" (patchedAnyVar = true) →
 * fallback de re-render não disparava → section certa não atualizava.
 *
 * Agora: `{ sectionType: { settingId: bindings[] } }`. Lookup exige
 * sectionType, então o patcher só toca vars declaradas no .liquid
 * daquela section type específica.
 */
let DISCOVERED_SECTION_CSS_VARS: Record<
  string,
  Record<string, SectionCssVarBinding[]>
> = {};

/**
 * Procura no Liquid linhas no formato:
 *   --<varname>: {{ section.settings.<id> ...filters... }}<unit?>;
 * Onde unit é opcional (px, em, rem, %, vh, etc.). Filtros (default,
 * divided_by, plus, etc.) são tolerados — qualquer coisa entre
 * `section.settings.X` e `}}`.
 *
 * Retorna mapa `{ settingId: [{var, unit, rootScoped}, ...] }`
 * deduplicado. Se a var aparece DUAS vezes no source — uma em
 * `:root { ... }` e outra em `#section-X { ... }` — o binding fica
 * com `rootScoped: true` (ANY occurrence inside `:root` wins, porque
 * implica que o snippet pai consome via `:root`).
 */
export function discoverSectionCssVarsFromLiquid(
  liquidSource: string
): Record<string, SectionCssVarBinding[]> {
  const result: Record<string, SectionCssVarBinding[]> = {};
  /*
    Comentários CSS removidos — same trick do discoverer global.
    Liquid comments também (`{% comment %}...{% endcomment %}`) pra
    evitar phantoms tipo `--var: {{ section.settings.X }}` que vivem
    em docstrings explicativas no topo do arquivo.
  */
  const stripped = liquidSource
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\{%-?\s*comment\s*-?%\}[\s\S]*?\{%-?\s*endcomment\s*-?%\}/g, '');

  /*
    Pra detectar scope (`:root` vs outros), achamos os ranges de
    `:root { ... }` no source — qualquer match dentro desses ranges
    é root-scoped. Loop simples balanceando `{` e `}` (o source pode
    ter vars com `{` em filtros liquid, mas como já strippamos
    comentários CSS, só sobra liquid syntax — `{{ ... }}` e
    `{% ... %}`. Ignoramos esses contando só `{` que vem direto
    depois de `:root` whitespace).
  */
  const rootRanges: Array<[number, number]> = [];
  const rootRe = /:root\s*\{/g;
  let rm: RegExpExecArray | null;
  while ((rm = rootRe.exec(stripped)) !== null) {
    const blockStart = rm.index + rm[0].length;
    let depth = 1;
    let i = blockStart;
    while (i < stripped.length && depth > 0) {
      const ch = stripped[i];
      // Pula `{{ ... }}` e `{% ... %}` pra não confundir contagem.
      if (ch === '{' && (stripped[i + 1] === '{' || stripped[i + 1] === '%')) {
        const closer = stripped[i + 1] === '{' ? '}}' : '%}';
        const closeIdx = stripped.indexOf(closer, i + 2);
        if (closeIdx === -1) break;
        i = closeIdx + 2;
        continue;
      }
      if (ch === '{') depth++;
      else if (ch === '}') depth--;
      i++;
    }
    rootRanges.push([blockStart, i]);
  }
  const isInRoot = (pos: number): boolean =>
    rootRanges.some(([s, e]) => pos >= s && pos < e);

  // Captura: var name, setting id, unit (px|em|rem|%|vh|vw|...) opcional
  // antes do `;`.
  const re = /--([a-zA-Z0-9_-]+)\s*:\s*\{\{[^}]*?section\.settings\.([a-zA-Z0-9_]+)[^}]*?\}\}([a-zA-Z%]*)\s*;/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(stripped)) !== null) {
    const varName = m[1];
    const settingId = m[2];
    const unit = m[3] || '';
    const rootScoped = isInRoot(m.index);
    if (!result[settingId]) result[settingId] = [];
    const existing = result[settingId].find(
      (b) => b.var === varName && b.unit === unit
    );
    if (existing) {
      // ANY :root declaration upgrades o binding pra rootScoped.
      if (rootScoped && !existing.rootScoped) existing.rootScoped = true;
    } else {
      result[settingId].push({ var: varName, unit, rootScoped });
    }
  }
  return result;
}

export function setDiscoveredSectionCssVars(
  map: Record<string, Record<string, SectionCssVarBinding[]>>
) {
  DISCOVERED_SECTION_CSS_VARS = map || {};
}

/**
 * Pra uma section setting, retorna lista de `{var, unit}` que devem ser
 * patchadas em `#section-<id>`. Vazio se a setting não tem nenhum CSS
 * var conhecido — caller cai pro re-render normal.
 *
 * Filtra por `sectionType` pra que dois `.liquid` que declaram settings
 * com mesmo `id` (ex.: `background` em contact-form e search-results)
 * não vazem entre si. Sem o filtro, mexer em qualquer "background"
 * patchava `--contact-bg` E `--search-bg`, e ainda fazia o fallback de
 * rerender achar que tudo deu certo (patchedAnyVar=true).
 */
export function sectionCssVarsFor(
  sectionType: string,
  settingId: string
): SectionCssVarBinding[] {
  return DISCOVERED_SECTION_CSS_VARS[sectionType]?.[settingId] || [];
}

/**
 * Formata o valor pro var. Se unit for não-vazio e o valor for
 * numérico (ou string numérica), concatena. Cores e strings genéricas
 * passam diretas. Boolean vira '1'/'0' (raro pra CSS var, mas safe).
 */
export function formatValueForSectionCssVar(
  value: any,
  unit: string
): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? '1' : '0';
  if (typeof value === 'number') {
    return unit ? `${value}${unit}` : String(value);
  }
  if (typeof value === 'string') {
    if (unit && /^[-+]?\d+(\.\d+)?$/.test(value.trim())) {
      return `${value}${unit}`;
    }
    return value;
  }
  return null;
}

/**
 * Junta o mapa hardcoded + o mapa auto-descoberto. O auto-descoberto
 * tem precedência (é 1:1 com o liquid que renderiza), e completamos com
 * o hardcoded pra cobrir settings que não vivem em css-variables.liquid
 * (ex. settings que alimentam vários snippets ou vars legacy).
 */
function resolveVarsFor(id: string): string[] {
  const fromLiquid = DISCOVERED_CSS_VARS[id] || [];
  const hardcoded = SETTING_TO_CSS_VAR[id];
  const hcList = hardcoded
    ? Array.isArray(hardcoded) ? hardcoded : [hardcoded]
    : [];
  if (fromLiquid.length === 0) return hcList;
  // Dedup, preservando ordem (liquid primeiro).
  const seen = new Set<string>();
  const out: string[] = [];
  for (const n of [...fromLiquid, ...hcList]) {
    if (!seen.has(n)) { seen.add(n); out.push(n); }
  }
  return out;
}

/**
 * Compute a patch of CSS vars from a `{ settingId: value }` object.
 * Returns the keys that need to be pushed onto `:root`, with values
 * already normalised (e.g. `14` → `14px` for font-size settings).
 * Settings we don't know how to map are dropped — the caller is
 * responsible for falling back to a section re-render for those.
 */
export function cssVarPatchFromSettings(
  settings: Record<string, any>
): Array<{ name: string; value: string }> {
  const patch: Array<{ name: string; value: string }> = [];
  for (const [id, value] of Object.entries(settings)) {
    const names = resolveVarsFor(id);
    if (names.length === 0) continue;
    const formatted = formatValueForCssVar(id, value);
    if (formatted === null) continue;
    for (const n of names) patch.push({ name: `--${n}`, value: formatted });
  }
  return patch;
}

/**
 * Apply a patch of CSS vars to the iframe's `:root`. Returns the keys
 * that were set so the caller can log or diff.
 */
export function applyCssVarPatch(
  doc: Document,
  patch: Array<{ name: string; value: string }>
): string[] {
  if (patch.length === 0) return [];
  const root = doc.documentElement;
  const applied: string[] = [];
  for (const { name, value } of patch) {
    /*
      `!important` no inline style do `<html>`. Sem isso, sections que
      declaram a mesma var num `:root { ... }` block do próprio
      `<style>` (ex.: header.liquid) podem ter precedência inesperada
      em alguns browsers/devtools — empiricamente o inline aparecia
      "riscado" no devtools.

      Bug reportado: lojista mudava header_background no inspector
      e o cabeçalho não atualizava — o `:root { --header-background: ... }`
      hardcoded no `<style>` bloqueava o setProperty inline.
    */
    root.style.setProperty(name, value, 'important');
    applied.push(name);
  }
  return applied;
}

/**
 * Return the set of setting ids that this module knows how to patch
 * live (i.e. that map to CSS vars). Callers use this to decide
 * whether a full section re-render is still needed for OTHER edits
 * mixed into the same change burst.
 */
export function knownCssVarSettings(): Set<string> {
  // União do hardcoded + auto-descoberto. Usado pelo scheduler pra
  // decidir quando um change é "fast-path CSS-only" vs "precisa re-render
  // de seção".
  const out = new Set(Object.keys(SETTING_TO_CSS_VAR));
  for (const id of Object.keys(DISCOVERED_CSS_VARS)) out.add(id);
  return out;
}
